import { useForm } from 'react-hook-form';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { Loader2, Save, BadgeCheck } from 'lucide-react';
import { api } from '@/api/client';

interface Kyc {
  id?: number;
  panNumber?: string;
  panFile?: string;
  gstNumber?: string;
  gstFile?: string;
  aadharNumber?: string;
  aadharFile?: string;
  isVerified?: boolean;
}

export function MerchantKycTab({ merchantId, merchantView = false }: { merchantId: number; merchantView?: boolean }) {
  const qc = useQueryClient();
  const { data, isLoading } = useQuery({
    queryKey: ['merchant-kyc', merchantId],
    queryFn: async () => {
      try {
        return (await api.get<Kyc>(`/merchants/${merchantId}/kyc`)).data;
      } catch { return {} as Kyc; }
    },
  });

  const { register, handleSubmit, reset } = useForm<Kyc>({ values: data ?? {} });
  // A merchant can fill KYC once; after it's submitted (PAN/GST added) or admin-verified it is
  // view-only for them — they must contact support to change it. Admins (merchantView=false) can still edit.
  const hasKyc = !!(data?.panNumber || data?.gstNumber);
  const readOnly = merchantView && (!!data?.isVerified || hasKyc);

  const save = useMutation({
    mutationFn: async (d: Kyc) => (await api.put(`/merchants/${merchantId}/kyc`, d)).data,
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['merchant-kyc', merchantId] }); toast.success('KYC saved'); },
    onError: (e: any) => toast.error(e?.response?.data?.message ?? 'Failed to save KYC'),
  });

  const verify = useMutation({
    mutationFn: async () => (await api.post(`/merchants/${merchantId}/kyc/verify`)).data,
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['merchant-kyc', merchantId] }); toast.success('KYC marked as verified'); },
    onError: (e: any) => toast.error(e?.response?.data?.message ?? 'Verification failed'),
  });

  if (isLoading) return <div className="flex justify-center py-8"><Loader2 size={20} className="animate-spin text-primary" /></div>;

  return (
    <form onSubmit={handleSubmit((d) => save.mutate(d))} className="space-y-5">
      {readOnly && (
        <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-3 text-sm text-emerald-800 flex items-center gap-2">
          <BadgeCheck size={16} /> {data?.isVerified
            ? 'Approved by admin — contact support to make changes.'
            : 'Your KYC details are submitted and locked. Contact support to make changes.'}
        </div>
      )}
      <div className="grid grid-cols-2 gap-4">
        <Pair label="PAN Number" placeholder="ABCDE1234F">
          <input {...register('panNumber')} disabled={readOnly} className="input uppercase disabled:bg-slate-50" />
        </Pair>
        <Pair label="PAN Document URL">
          <input {...register('panFile')} disabled={readOnly} placeholder="https://..." className="input disabled:bg-slate-50" />
        </Pair>
        <Pair label="GST Number" placeholder="22AAAAA0000A1Z5">
          <input {...register('gstNumber')} disabled={readOnly} className="input uppercase disabled:bg-slate-50" />
        </Pair>
        <Pair label="GST Document URL">
          <input {...register('gstFile')} disabled={readOnly} placeholder="https://..." className="input disabled:bg-slate-50" />
        </Pair>
        <Pair label="Aadhaar Number" placeholder="1234 5678 9012">
          <input {...register('aadharNumber')} disabled={readOnly} className="input disabled:bg-slate-50" />
        </Pair>
        <Pair label="Aadhaar Document URL">
          <input {...register('aadharFile')} disabled={readOnly} placeholder="https://..." className="input disabled:bg-slate-50" />
        </Pair>
      </div>

      <div className="flex items-center justify-between border-t border-slate-100 pt-4">
        <div className="flex items-center gap-2 text-sm">
          {data?.isVerified ? (
            <span className="inline-flex items-center gap-1 text-green-700"><BadgeCheck size={16} /> Verified</span>
          ) : (
            <span className="text-amber-700">Unverified</span>
          )}
          {!data?.isVerified && !merchantView && (
            <button type="button" onClick={() => verify.mutate()} disabled={verify.isPending}
              className="ml-3 px-3 py-1 text-xs border border-green-300 text-green-700 rounded hover:bg-green-50 disabled:opacity-60">
              {verify.isPending ? 'Verifying...' : 'Mark as Verified'}
            </button>
          )}
        </div>
        {!readOnly && <div className="flex items-center gap-2">
          <button type="button" onClick={() => reset(data ?? {})} className="px-3 py-2 text-sm text-slate-600 hover:bg-slate-100 rounded-lg">Reset</button>
          <button type="submit" disabled={save.isPending}
            className="px-4 py-2 bg-primary text-white rounded-lg font-medium hover:bg-primary-700 transition disabled:opacity-60 flex items-center gap-2 text-sm">
            {save.isPending ? <Loader2 size={14} className="animate-spin" /> : <Save size={14} />}
            Save KYC
          </button>
        </div>}
      </div>
    </form>
  );
}

function Pair({ label, placeholder, children }: { label: string; placeholder?: string; children: React.ReactNode }) {
  return (
    <div>
      <label className="block text-xs font-medium text-slate-600 mb-1">{label}{placeholder ? <span className="text-slate-400 font-normal"> · {placeholder}</span> : null}</label>
      {children}
    </div>
  );
}

